/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../../view/features/content/contentViewDom/api/impl/ContentViewDomImpl',
	'../../../lib/@waca/baglass/js/baglass/utils/Utils',
	'jquery',
	'underscore',
	'../../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'../../../app/nls/StringResources',
	'../../widgets/PropertiesUtil',
	'react-dom',
	'../../../lib/@waca/dashboard-common/dist/ui/CenterLoadingSpinner',
	'../../../api/impl/Layout',
	'../LayoutHelper',
	'../../../lib/@waca/core-client/js/core-client/ui/dialogs/ConfirmationDialog',
	'../../widgets/PropertyListUtil',
	'../../util/ContentRegistryUtil',
	'../../../app/util/UnderscoreExt'
], function(Class, ContentViewDomImpl, Utils, $, _, ClassFactory, StringResources, PropertiesUtil, ReactDOM, CenterLoadingSpinner, LayoutApi, LayoutHelper, ConfirmationDialog, PropertiesListUtil, ContentRegistryUtil) {

	const MINIMUM_PAGESIZE = { width: 320, height: 200 };
	const PAGESIZES = {
		'16:9': {
			'width': 1280,
			'height': 720
		},
		'4:3': {
			'width': 1280,
			'height': 960
		},
		'letter': {
			'width': 612,
			'height': 792
		},
		'legal': {
			'width': 612,
			'height': 1008
		},
		'a4': {
			'width': 595,
			'height': 842
		},
		'tabloid': {
			'width': 792,
			'height': 1224
		},
		'infographic': {
			'width': 512,
			'height': 1024
		}
	};

	const BaseLayout = Class.extend({
		init: function(options) {
			BaseLayout.inherited('init', this, arguments);
			this.layoutController = options.layoutController;
			this.isAuthoringMode = this.layoutController.isAuthoring;
			this.model = options.model;
			this.id = this.model.id;
			this.parentLayout = options.parentLayout;
			this.glassContext = options.services.biGlass.glassContext;
			this.appSettings = options.appSettings;
			this.dashboardApi = options.dashboardApi;
			this.additionalWidgetData = options.additionalWidgetData;
			this.contentFeatureLoader = options.contentFeatureLoader;
			this.content = options.content;
			if (this.dashboardApi) {
				this.colorsService = this.dashboardApi.getFeature('Colors');
				this.translationService = this.dashboardApi.getDashboardCoreSvc('TranslationService');
				this.contentTypeRegistry = this.dashboardApi.getFeature('ContentTypeRegistry');
			}
			const nodeId = this.layoutController.modelIdToNodeId(this.id);
			let widgetExistsInDOM = false;
			if (this.parentLayout) {
				this.domNode = $(this.parentLayout.domNode).find('#' + nodeId)[0];
				// If a widget has already been loaded into the DOM (outside canvas)
				// bring it in the canvas
				if (!this.domNode) {
					this.domNode = document.getElementById(nodeId);
					if (this.domNode) {
						widgetExistsInDOM = true;
					}
				}
			} else {
				this.domNode = document.getElementById(nodeId);
			}
			this._attachedHandles = [];
			if (this._isTopLevelPage()) {
				this._attachedHandles.push(this.model.on('change:pageSize', this.onPageSizeChange.bind(this), this));
			}

			// If the node does not exist, then we create one
			const htmlTemplate = options.services.getSvcSync('htmlTemplates');
			if (!this.domNode) {
				this.domNode = $(htmlTemplate.getHtml(this.model, this.model.boardModel.widgetInstances))[0];
			} else if (widgetExistsInDOM) {
				//bring existing widget into a layout view
				const n = $(this.domNode);
				const children = n.children();
				const prev = n;
				this.domNode = $(htmlTemplate.getHtml(this.model, this.model.boardModel.widgetInstances))[0];
				const n$ = $(this.domNode);
				children.appendTo(n$);
				prev.remove();

				// resize the node since it previously existed outside of the canvas.
				this.layoutController.layoutReady(this.model.id).then(this.onResize.bind(this));
			}
			this.domNode._layout = this;

			this.authorHelper = options.authorHelper; //author mode addon

			//static widgets end up using a LayoutBaseView (not a 'Widget') when moving to author mode
			//need to save onto the widget chrome event router
			this.widgetChromeEventRouter = options.widgetChromeEventRouter;
			this.widgetChromeEventRouter_disposeEvents = [];

			this.$el = $(this.domNode);

			this.logger = this.glassContext.getCoreSvc('.Logger');
			this.services = options.services;

			this.api = new LayoutApi(this);
			if (!this.eventRouter) {
				this.eventRouter = this.layoutController.eventRouter;
				if(this.eventRouter) {
					this.eventRouter.on('properties:hasRendered', this.setInitialEnabledState, this);
					this._enableToggleEvents();

					// While it would be better to listen to change:layoutPositioning and change:pageSize events,
					// those are listened to by the child layout classes and need to be processed before we resize
					// the layout.  Listen to this event which will be fired after the properties are modified.
					this.eventRouter.on('layout:resize', this._onResize, this);
				}
			}
			this._debouncedOnResize = _.doubleDebounce(this._onResize.bind(this), 200);

			this._subLayoutViews = [];
		},

		setInitialEnabledState: function(){
			// TODO : using events to achieve this seems wrong and hacky. We must find a better way to achieve this
			if(this.model.getValueFromSelfOrParent('layoutPositioning') !== 'relative'){
				this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'fitPage', enabled: false });
			}
		},

		_enableToggleEvents() {
			this.model.on('change:fitPage', this._updatePropertiesPaneToggle, this);
			this.model.on('change:showGrid', this._updatePropertiesPaneToggle, this);
			this.model.on('change:snapGrid', this._updatePropertiesPaneToggle, this);
			this.model.on('change:snapObjects', this._updatePropertiesPaneToggle, this);
		},

		_disableToggleEvents() {
			this.model.off('change:fitPage', this._updatePropertiesPaneToggle.bind(this), this);
			this.model.off('change:showGrid', this._updatePropertiesPaneToggle.bind(this), this);
			this.model.off('change:snapGrid', this._updatePropertiesPaneToggle.bind(this), this);
			this.model.off('change:snapObjects', this._updatePropertiesPaneToggle.bind(this), this);
		},

		/**
		 * Updates the state of a properties pane toggle after an undo/redo
		 */
		_updatePropertiesPaneToggle(event) {
			const checked = event.value || false;
			this.eventRouter.trigger('properties:setValue', { propertyName: event.name, value: checked });
		},

		/**
		 * Return the id of the node associated with the model id. If we start prefixing the node, this method can calculate the proper node id.
		 */
		getNodeId: function(modelId) {
			return this.layoutController.modelIdToNodeId(modelId);
		},

		_destroyAuthoringHelper: function() {
			if (this.authorHelper) {
				this.authorHelper.destroy();
				this.authorHelper = null;
			}
		},

		destroy: function(event) {

			this._subLayoutViews = null;

			this.widgetChromeEventRouter_disposeEvents.forEach(dispose => dispose.remove());
			this.widgetChromeEventRouter_disposeEvents = [];



			if (this._debouncedOnResize){
				this._debouncedOnResize.cancel();
			}

			this._destroyAuthoringHelper();

			if (this.eventRouter) {
				this.eventRouter.off('properties:hasRendered', this.setInitialEnabledState, this);
				this._disableToggleEvents();
				this.eventRouter.off('layout:resize', this._onResize, this);
			}

			this._attachedHandles.forEach(handle => handle.remove());
			this._attachedHandles = [];

			if (this.domNode) {

				const $domNode = $(this.domNode);
				$domNode.off('contextmenu.baselayout');

				$('.page,.widget', $domNode).each(function() {
					if (this._layout && this._layout.destroy) {
						this._layout.destroy(event);
					}
				});

				$domNode.remove();
				this.domNode._layout = null;
			}

			if (this._contentTitle) {
				this._contentTitle = null;
			}

			if (this.layoutController) {
				this.layoutController.removeView(this);
			}
			if (this.api){
				this.api.destroy();
				this.api = null;
			}
			this.layoutController = null;
			this.model = null;
			this.id = null;
			this.parentLayout = null;
			this.glassContext = null;
			this.appSettings = null;
			this.dashboardApi = null;
			this.colorsService = null;
			this.translationService = null;
			this.additionalWidgetData = null;
			this.contentFeatureLoader = null;
			this.content = null;
			this.widgetChromeEventRouter = null;
			this.logger = null;
			this.services = null;
			this.authorHelper = null;
			this.domNode = null;
			this.layoutController = null;
			this._debouncedOnResize = null;

			BaseLayout.inherited('destroy', this, arguments);
		},

		_renderContentTitle() {
			const capabilitiesFeature = ContentRegistryUtil.getCapabilities(this.content);
			const supportTitle = capabilitiesFeature ? capabilitiesFeature.getCapabilities()['title'] : false;

			if (supportTitle) {
				this._contentTitle = this.content.getFeature('ContentTitle.internal');
				this.$el.addClass('titleSupported');
				return this._contentTitle.render({
					id: this.domNode.id,
					model: this.model,
					widgetChromeEventRouter: this.widgetChromeEventRouter,
					eventRouter: this.eventRouter
				});
			}
		},

		// lifecycle_cleanup -- this should render but we currently have a render that call onShow.
		// clean this up
		renderContent: function() {
			const stateApi = this.content.getFeature('state.internal');
			stateApi.setStatus(stateApi.STATUS.RENDERING);

			if (this._isRendered) {
				const promises = this._subLayoutViews
					.map((layout) => layout.renderContent());
				return Promise.all(promises)
					.then(() => {
						stateApi.setStatus(stateApi.STATUS.RENDERED);
					});
			} else {

				const promises = [];

				const renderer = this.content.getFeature('renderer');
				if (renderer) {
					const childNode = this.domNode;
					childNode.className = `widget content ${this.content.getType()}`;
					const canRotate = this.content.getPropertyValue('canRotate');
					if (!canRotate) {
						childNode.className += ' noRotate';
					}
					const rendererPromise = renderer
						.render({
							parent: childNode
						})
						.then(() => {

							const titleContent = this._renderContentTitle();
							if (titleContent) {
								childNode.prepend(titleContent);
							}

							this.contentFeatureLoader
								.registerFeature(this.content.getId(), 'ContentViewDOM', new ContentViewDomImpl(childNode));
						});
					promises.push(rendererPromise);
				}

				(this.model.items || [])
					.forEach(({ id }) => {
						promises.push(
							this.layoutController.createLayoutModule(id, this, this.additionalWidgetData)
								.then((layout) => {
									if (layout) {
										this._subLayoutViews.push(layout);
										return layout.renderContent();
									}
								})
						);
					});

				return Promise.all(promises)
					.then(() => this.registerViewFeatures())
					.then(() => {
						stateApi.setStatus(stateApi.STATUS.RENDERED);
						this.layoutController.markViewAsReady(this);
						this._isRendered = true;
					});
			}
		},

		registerViewFeatures(){
			// this will be called from the render content.. which might be called more than once as we switch between author and consume mode
			// make sure we only call it once.
			if (!this._viewFeatureRegistered){
				this._viewFeatureRegistered = true;
				// Add a view dom feature and then register the content-view-features collection
				return this.contentFeatureLoader.registerFeature(this.id, 'ContentViewDOM', new ContentViewDomImpl(this.domNode)).then(() => {
					return this.contentFeatureLoader.registerFeatureCollection(this.id, 'com.ibm.bi.dashboard.content-view-features');
				});
			} else {
				return Promise.resolve();
			}
		},


		onResize: function(renderOptions) {
			this._debouncedOnResize(renderOptions);
		},

		_onResize: function(renderOptions) {
			if (this.model.items) {
				_.each(this.model.items, (item) => {
					const view = this.layoutController.getLayoutView(item.id);
					if (view && view.onResize) {
						// do not try re-sizing hidden elements, as their sizes won't be accurate
						if (!view.$el.is(':hidden')) {
							view.onResize(renderOptions);
						} else {
							view.resizeOnShow();
						}
					}
				});
			}
		},

		/**
		 * Invoked when Widget chrome is selected, derived classes to override
		 */
		onSelect: function(isGroupSelect) {
			if (this.contentTypeRegistry.isTypeRegistered(this.content.getType()) && this.widgetChromeEventRouter && this.isAuthoringMode && !isGroupSelect) {
				this.widgetChromeEventRouter.trigger('title:chromeSelected');
			}
		},

		/**
		 * Invoked when Widget chrome is deselected, derived classes to override
		 */
		onDeselect: function() {
			if (this.contentTypeRegistry.isTypeRegistered(this.content.getType()) && this.widgetChromeEventRouter && this.isAuthoringMode) {
				this.widgetChromeEventRouter.trigger('title:chromeDeselected');
			}
		},

		/**
		 * Invoked when a hidden layout is shown
		 */
		onShow: function() {

			this._callChildrenFunction('onShow');

			if (this.isResizeOnShow) {
				this.isResizeOnShow = false;
				this.onResize();
			}
		},

		/**
		 * Invoke when a layout is hidden
		 */
		onHide: function() {
			this._callChildrenFunction('onHide');
		},

		/**
		 * Invoke when a is transitioning from one view to another.
		 */
		onTransition: function(event) {
			return this._callChildrenFunctionAsync('onTransition', event);
		},

		/**
		 * Invoke when a layout has transitioned.
		 */
		afterTransition: function(event) {
			return this._callChildrenFunctionAsync('afterTransition', event);
		},

		/**
		 * Indicates that we need to call resize the next time onShow is called.
		 */
		resizeOnShow: function() {
			this.isResizeOnShow = true;
		},

		_callChildrenFunctionAsync: function(name, event) {
			const promises = [];
			_.each(this.model.items, (item) => {
				const view = this.layoutController.getLayoutView(item.id);
				if (view && view[name]) {
					promises.push(view[name](event));
				}
			});
			return Promise.all(promises);
		},

		_callChildrenFunction: function(name) {
			_.each(this.model.items, (item) => {
				const view = this.layoutController.getLayoutView(item.id);
				if (view && view[name]) {
					view[name]();
				}
			});
		},

		getProperties: function() {
			let items = [];
			items.push(this.getBannerProperty());
			if (this._isTopLevelPage()){
				// If this is the top level layout, then we add the dashboard properties
				items.push(...this._getCanvasProperties());

				return this._fetchThemes().then((themeListing) => {
					items.push({
						type: 'DropDown',
						label: StringResources.get('propTheme'),
						name: 'theme',
						id: 'theme',
						defaultValue: this.layoutController.boardModel.get('theme'),
						options: themeListing,
						tabName: StringResources.get('tabName_general'),
						sectionName: StringResources.get('sectionName_colorsAndThemes'),
						sectionPosition: 2,
						showLabels: true,
						onChange: (propertyName, propertyValue) => {
							this.layoutController.boardModel.set({
								'theme': propertyValue
							});
						}
					});

					return Promise.all([
						this._getDashboardColorSetPropertySpec(),
						this._getBackgroundColorPropertySpec()
					]).then((propertySpecs) => {
						items = items.concat(propertySpecs);
						items.push(this._getFredIsRedSpec());
						items.push(this._getDataCacheSpec());
						if (this.translationService.getDefaultLanguage() === 'Default') {
							this._addLanguageSpecWhenNoLocaleSet(items);
						} else {
							this._addMultilingualProperties(items);
						}

						return items;
					});
				}).catch((error) => {
					this.logger.error(error, this);
					throw error;
				});

			} else {
				// Not a top level layout (e.g. embedded layout that is not a widget)
				// We only support the background color for now
				return this._getBackgroundColorPropertySpec().then( (backgroundSpec) =>{
					items.push(backgroundSpec);
					return items;
				});
			}
		},

		_getDashboardColorSetPropertySpec: function () {
			const dashboardColorSet = {
				id: 'dashboardColorSet',
				name: 'dashboardColorSet',
				type: 'NewPalette',
				sectionLabel: StringResources.get('propDashboardColorPalette'),
				newPaletteLabel: StringResources.get('propNewDashboardPaletteLabel'),
				linkLabel: StringResources.get('propDashboardChangePaletteLink'),
				ariaLabel: StringResources.get('propDashboardColorPalette'),
				tabName: StringResources.get('tabName_general'),
				sectionName: StringResources.get('sectionName_colorsAndThemes'),
				paletteType: 'ColorPalette',
				defaultValue: 'userPaletteColor',
				includeUserColorPalette: true
			};

			const onChangeCallback = (propertyName, paletteItem) => {
				this.layoutController.boardModel.get('properties').set({
					dashboardColorSet: paletteItem.id
				});

				this.eventRouter.trigger('properties:refreshPane', {
					'sender': this.layoutController.boardModel.get('properties').id
				});
			};

			return PropertiesUtil.handleNewPaletteProperty(dashboardColorSet, this.layoutController.boardModel.get('properties'), this.dashboardApi, onChangeCallback);
		},

		_getBackgroundColorPropertySpec: function() {
			const colorPickerSpec = {
				'name': 'fillColor',
				'id': 'fillColor',
				'type': 'ColorPicker',
				'label': StringResources.get('propDashboardBackgroundColor'),
				'open': false,
				'ariaLabel': StringResources.get('propDashboardBackgroundColor'),
				'paletteType': 'DashboardColorSet',
				'showHexValue': true,
				'addButton': true,
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_colorsAndThemes'),
				'defaultValue': 'transparent'
			};
			return PropertiesUtil.handleColorPickerProperty(colorPickerSpec, this, this.dashboardApi).then((colorPickerSpec) => {
				colorPickerSpec.onChange = (propertyName, propertyValueInfo) => {
					// get layout and update CSS
					const oLayout = this.model;
					const data = {
						undoRedoTransactionId: _.uniqueId('layout_fillColorChange_')
					};

					const payload = {
						fillColor: propertyValueInfo.name
					};

					// This step is done because we used to set the "css" model property to have the fill color classname in addition to fillColor property
					// This is bad because it was overriding the css values that are part of the layout definition
					// We stopped doing this, but the following code is to handle an older dashboard that has the fill set in the css property (e.g. css = fill-color1)
					// We either have an upgrade to remove the layout 'css' property that contains the fill color, or we do it here.
					if (oLayout.get('css') === 'fill-' + oLayout.get('fillColor')){
						// If the css was prreviously set , just remove it
						payload.css = undefined;
					}

					this.colorsService.prepareForColorModelChange(payload, 'fillColor');

					oLayout.set(payload, {
						sender: this.senderId,
						payloadData: data
					});


				};

				return colorPickerSpec;
			});

		},

		_getFredIsRedSpec() {
			return {
				type: 'ToggleButton',
				label: StringResources.get('propFredIsRed'),
				name: 'fredIsRed',
				id: 'fredIsRed',
				checked: this.model.boardModel && this.model.boardModel.properties && this.model.boardModel.properties.fredIsRed,
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_advanced'),
				onChange: (propertyName, propertyValue) => {
					this.layoutController.boardModel.properties.set({
						'fredIsRed': propertyValue
					});
				}
			};
		},

		_getDataCacheSpec: function() {
			return {
				'type': 'DropDown',
				'label': StringResources.get('dataCache'),
				'name': 'dataCache',
				'id': 'dataCache',
				'defaultValue': this.model.boardModel && this.model.boardModel.properties && this.model.boardModel.properties.localCache || 'automatic',
				'options': [
					{
						label: StringResources.get('dataCache_auto'),
						value: 'automatic'
					},
					{
						label: StringResources.get('dataCache_on'),
						value: 'yes'
					},
					{
						label: StringResources.get('dataCache_off'),
						value: 'no'
					}
				],
				sectionPosition: 99,
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_advanced'),
				onChange: (propertyName, propertyValue) => {
					if ( propertyValue === 'automatic' ) {
						this.model.boardModel.properties.set({
							'localCache': undefined
						});
					} else {
						this.model.boardModel.properties.set({
							'localCache': propertyValue
						});
					}
				}
			};
		},

		_addMultilingualProperties: function (properties) {
			const defaultLanguage = this.translationService.getDefaultLanguage();
			const tabName = StringResources.get('tabName_general');

			properties.push({
				type: 'SingleLineLinks',
				tabName: tabName,
				name: 'Languages',
				sectionName: StringResources.get('sectionName_advanced'),
				disabled: this.translationService.translationModeOn,
				items: [{
					align: 'left',
					items: [{
						type: 'text',
						value: StringResources.get('multilingualDashboardsLabel')
					}]
				}]
			});

			// Create the Default language row - it's always first
			properties.push(this._createLanguageRow(defaultLanguage, tabName, true));

			// Rows for all other available languages
			this.translationService.getSelectedLanguages().sort((a, b) => {
				return this.translationService.getLanguageName(a) > this.translationService.getLanguageName(b) ? 1 : -1;
			}).forEach((language) => {
				if (language !== defaultLanguage && language !== 'Default') {
					properties.push(this._createLanguageRow(language, tabName, false));
				}
			});

			properties.push({
				type: 'SingleLineLinks',
				tabName: tabName,
				name: 'addLanguage',
				sectionName: StringResources.get('sectionName_advanced'),
				disabled: this.translationService.translationModeOn,
				items: [{
					align: 'right',
					items: [{
						type: 'text',
						value: StringResources.get('multilingualDashboardsAddLinkText'),
						clickCallback: () => {
							this.launchLanguageSelectSlideout(null, true);
						}
					}]
				}]
			});

			return properties;
		},

		/**
		 * Language property spec when no language has been selected yet
		 */
		_addLanguageSpecWhenNoLocaleSet: function (properties) {
			const tabName = StringResources.get('tabName_general');
			const defaultLanguage = this.translationService.getDefaultLanguage();
			properties.push({
				type: 'SingleLineLinks',
				tabName: tabName,
				name: 'language_' + defaultLanguage,
				sectionName: StringResources.get('sectionName_advanced'),
				items: [{
					align: 'left',
					items: [{
						type: 'text',
						id: 'text' + defaultLanguage,
						value: StringResources.get('multilingualDashboardsLabel')
					}]
				}, {
					align: 'right',
					items: [{
						type: 'text',
						value: StringResources.get('multilingualDashboardsSetDefault'),
						clickCallback: () => {
							this.launchLanguageSelectSlideout(defaultLanguage, false);
						}
					}]
				}]
			});
		},

		/**
		 * Creats a SingleLineLink property spec that describes a language
		 */
		_createLanguageRow: function (locale, tabName, isDefaultLocale) {
			const leftItems = [];
			const rightItems = [];
			const languageName = this.translationService.getLanguageName(locale);
			rightItems.push({
				type: 'text',
				id: 'text' + locale,
				value: isDefaultLocale ?
					StringResources.get('multilingualDashboardsDefault', { defaultLanguage: languageName }) :
					languageName
			});
			rightItems.push({
				type: 'icon',
				svgIcon: 'common-menuoverflow',
				iconTooltip: StringResources.get('multilingualDashboardIconMore', {
					currentLanguage: languageName
				}),
				clickCallback: (evt) => {
					this.launchLanguageMenu(evt, locale);
				}
			});

			return {
				type: 'SingleLineLinks',
				tabName: tabName,
				id: 'language_' + locale,
				sectionName: StringResources.get('sectionName_advanced'),
				indent: 2,
				disabled: this.translationService.translationModeOn,
				items: [{
					align: 'left',
					items: leftItems
				}, {
					align: 'right',
					items: rightItems
				}]
			};
		},

		/**
		 * When picking a language, we launch an overlay slideout with all the content locales
		 */
		launchLanguageSelectSlideout: function (selectedLocale, addLanguage) {
			const propertiesManager = this.layoutController.canvasController.getExtension('propertiesManager');
			propertiesManager.addChild({
				overlay: true,
				label: StringResources.get('multilingualSelectLanguage'),
				content: {
					module: 'dashboard-core/js/dashboard/contentpane/PropertyUIControlView',
					items: [{
						value: StringResources.get('multilingualSelectLanguage'),
						centerLabel: true,
						type: 'Banner',
						backButton: true,
						ariaLabel: StringResources.get('multilingualSelectLanguage')
					}, {
						type: 'RadioButtonGroup',
						name: 'language',
						separator: true,
						ariaLabel: StringResources.get('multilingualSelectLanguage'),
						value: selectedLocale,
						selectOnNavigation: false,
						items: this.translationService.getLanguageRadioItems(),
						onChange: (name, newLocale) => {
							propertiesManager.closeChild();
							if (selectedLocale === 'Default') {
								this.translationService.changeDefaultLanguage(newLocale);
							} else if (addLanguage) {
								this.translationService.startTranslationMode(newLocale);
							} else {
								this.translationService.switchLanguage({
									from: selectedLocale,
									to: newLocale
								});
							}
						}
					}]
				}
			});
		},

		/**
		 * User clicked on the ..., so show the menu
		 */
		launchLanguageMenu: function (evt, locale) {
			let position = {};
			if (evt.pageX === undefined ||
				(evt.gesture && (evt.gesture.center === undefined || evt.gesture.center.pageX === undefined))) {
				position = $(evt.target).offset();
			} else {
				position.left = evt.pageX || evt.gesture.center.pageX;
				position.top = evt.pageY || evt.gesture.center.pageY;
			}

			this.glassContext.appController.showContextMenu({
				position: {
					pageX: position.left,
					pageY: position.top
				},
				menuId: 'com.ibm.bi.dashboard.languageMenu',
				activeObject: {
					selectedLocale: locale,
					translationService: this.translationService,
					layoutBaseView: this
				}
			});
		},

		_isTopLevelPage: function() {
			return this.model && this.layoutController.boardModel && this.model === this.layoutController.boardModel.layout;
		},

		getBannerProperty: function() {
			return {
				'value': this._isTopLevelPage() ? StringResources.get('dashboardProperties'): StringResources.get('settings')  ,
				'name': 'banner',
				'type': 'Banner',
				'editable': false
			};
		},

		_getCanvasProperties: function() {
			let items = [this._getLayoutPositioningSpec()];
			items = items.concat(this._getPageSizeSpec());
			items = items.concat(this._getGridOptionsSpec());

			return items;
		},

		_getLayoutPositioningSpec: function() {
			let defaultValue = this._getLayoutPositioning();
			let layoutPositioningOptions = this._getLayoutPositioningSpecOptions(defaultValue);
			let _this = this;

			return {
				'type': 'DropDown',
				'label': StringResources.get('propLayoutPositioning'),
				'name': 'layoutPositioning',
				'id': 'layoutPositioning',
				'defaultValue': defaultValue,
				'options': layoutPositioningOptions,
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas'),
				sectionPosition: 1,
				'coachMark': {
					render: (options) => {
						Utils.addCoachmark({
							id: 'com.ibm.bi.dashboard.dashboardCoreProperties.layoutPositioning',
							title: StringResources.get('propLayoutPositioningCoachmarkTitle'),
							contents: StringResources.get('propLayoutPositioningCoachmarkContent'),
							placement: 'bottom',
							glassContext: options.glassContext,
							$el: options.$el
						});
					}
				},
				onChange: function(propertyName, propertyValue) {
					_this._onChangeLayoutPosition(this, propertyValue);
				}
			};
		},

		_getLayoutPositioningSpecOptions: function(defaultValue) {
			let layoutPositioningOptions = [];
			if (!defaultValue) {
				layoutPositioningOptions.push({
					label: '',
					value: 'undefined'
				});
			}
			layoutPositioningOptions.push({
				label: StringResources.get('propLayoutRelative'),
				value: 'relative'
			},
			{
				label: StringResources.get('propLayoutAbsolute'),
				value: 'absolute'
			});
			return layoutPositioningOptions;
		},

		_getLayoutPositioning: function() {
			return this.model.getValueFromSelfOrParent('layoutPositioning');
		},

		_setLayoutPositioning: function(value) {
			const data = {
				undoRedoTransactionId: _.uniqueId('layout_layoutPositioningChange_')
			};

			this.model.set(
				{ layoutPositioning: value },
				{
					payloadData: data,
					sender: this.senderId
				}
			);

			this.onResize();
		},

		_setFitPageLayoutPositioning: function (layoutPos, fitPage) {
			const data = {
				undoRedoTransactionId: _.uniqueId('layout_layoutPositioningChange_')
			};
			this.model.set(
				{
					layoutPositioning: layoutPos,
					fitPage: fitPage
				},
				{
					payloadData: data,
					sender: this.senderId
				}
			);
			this.onResize();
		},

		_onChangeLayoutPosition: function(property, propertyValue) {
			let oldLayoutPositioning = this._getLayoutPositioning();
			if (oldLayoutPositioning) {
				if(propertyValue === 'absolute'){
					this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'fitPage', enabled: false });
					this.eventRouter.trigger('properties:setValue', { propertyName: 'fitPage', value: false });
					this._setFitPageLayoutPositioning(propertyValue, false);
				} else {
					this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'fitPage', enabled: true });
					let pageSize = this.model.get('pageSize');
					let preset = this._getPageSizePreset(pageSize.width, pageSize.height);

					if (preset === '16:9' || preset === '4:3') {
						this.eventRouter.trigger('properties:setValue', { propertyName: 'fitPage', value: true });
						this._setFitPageLayoutPositioning(propertyValue, true);
					} else {
						this.eventRouter.trigger('properties:setValue', { propertyName: 'fitPage', value: false });
						this._setFitPageLayoutPositioning(propertyValue, false);
					}
				}
			} else {
				// Confirm layout upgrade with user
				const onOK = () => {
					this._setLayoutPositioning(propertyValue);
					// Update property control - which should remove "undefined" as an option
					// Ideally we should only be updating the one control of interest.
					// But, this doesn't currently work right for items in a section block.
					// let event = {
					// 	'propertySpec': this._getLayoutPositioningSpec(),
					// 	'removeProperty': false
					// };
					// this.eventRouter.trigger('properties:refreshProperty', event);
					this.eventRouter.trigger('properties:refreshPane');
				};
				const onCancel = () => {
					// Reset back to undefined state
					property._lastChangedValue = 'undefined';  // Bug in glass, setValue doesn't reset _lastChangedValue.
					property.setValue('undefined');
				};
				let oConfirmationDialog = new ConfirmationDialog('warning', StringResources.get('confirmLayoutStyleUpgradeTitle'), StringResources.get('confirmLayoutStyleUpgrade'));
				oConfirmationDialog.confirm(onOK, onCancel);
			}
		},

		_getGridOptionsSpec: function(){
			return [{
				type: 'SectionLabel',
				label: StringResources.get('gridLabel'),
				id: 'gridLabel',
				name: 'gridLabel',
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas')
			},
			{
				type: 'ToggleButton',
				label: StringResources.get('gridText'),
				name: 'showGrid',
				id: 'showGrid',
				checked: (this.model.get('showGrid') === undefined) ? false : this.model.get('showGrid'),
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas'),
				onChange: (propertyName, propertyValue) => {
					this.model.set({
						'showGrid': propertyValue
					});
				}
			},{
				type: 'ToggleButton',
				label: StringResources.get('snapText'),
				name: 'snapGrid',
				id: 'snapGrid',
				checked: (this.model.get('snapGrid') === undefined) ? false : this.model.get('snapGrid'),
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas'),
				onChange: (propertyName, propertyValue) => {
					this.model.set({
						'snapGrid': propertyValue
					});
				}
			},{
				type: 'ToggleButton',
				label: StringResources.get('snapObjectsText'),
				name: 'snapObjects',
				id: 'snapObjects',
				checked: (this.model.get('snapObjects') === undefined) ? true : this.model.get('snapObjects'),
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas'),
				onChange: (propertyName, propertyValue) => {
					this.model.set({
						'snapObjects': propertyValue
					});
				}
			}];
		},

		_getPageSizePreset: function(width, height) {
			let preset = 'custom';
			const checkWidth = parseInt(width, 10),
				checkHeight = parseInt(height, 10);

			for (let value in PAGESIZES) {
				if (PAGESIZES.hasOwnProperty(value)) {
					const currentPreset = PAGESIZES[value];
					if (currentPreset.width === checkWidth && currentPreset.height === checkHeight) {
						preset = value;
						break;
					}
				}
			}

			return preset;
		},

		_wrapPageSizeTextProperty: function() {
			const action = this;
			return function() {
				let args = [action, ...arguments];
				action._pageSizeTextOnChange.apply(this, args);
			};
		},

		_pageSizeTextOnChange: function(action, id, value) {
			if (this.propertiesManagerChange) {
				this.onChangeValueHold = value;
			} else {
				let numericValue = parseInt(value, 10);
				const model = action.model;
				const direction = id==='pageSizeWidth' ? 'width' : 'height';
				let newPageSize = {};
				if (model.pageSize) {
					newPageSize = _.extend(newPageSize, model.pageSize);
				}

				if (!isNaN(numericValue) && newPageSize[direction] !== numericValue) {
					newPageSize[direction] = numericValue;
					newPageSize = action._setPageSize(newPageSize);
					numericValue = newPageSize[direction];
				}

				const propertyNameList = ['pageSizeSplit', id];
				if (isNaN(numericValue)) {
					action.eventRouter.trigger('properties:setValue', { propertyName: propertyNameList, value: this.onChangeValueHold });
				} else if (value !== this.onChangeValueHold) {
					this.onChangeValueHold = PropertiesListUtil.getPropertyDisplayString(numericValue, StringResources.get('pixelUnit'), true);
					action.eventRouter.trigger('properties:setValue', { propertyName: propertyNameList, value: this.onChangeValueHold });
				}
			}
		},

		_setPageSize: function(newSize, undoRedoTransactionId) {
			const model = this.model;
			let sizeChanged = false;
			const minimumSize = LayoutHelper.getBoundingPageSize(model, MINIMUM_PAGESIZE);
			if (minimumSize) {
				if (isNaN(newSize.width) || minimumSize.width > newSize.width) {
					newSize.width = minimumSize.width;
					sizeChanged = true;
				}
				if (isNaN(newSize.height) || minimumSize.height > newSize.height) {
					newSize.height = minimumSize.height;
					sizeChanged = true;
				}

				if (sizeChanged) {
					this.layoutController.glassContext.appController.showToast(StringResources.get('propPageSizeAdjustedMessage'), {
						type: 'info'
					});
				}
			}

			model.set({
				'pageSize': newSize
			}, {
				payloadData: { undoRedoTransactionId: undoRedoTransactionId }
			});

			this.onResize();

			return newSize;
		},

		_getPageSizeDisplayValues: function() {
			const model = this.model;
			const widthValue = model.pageSize && model.pageSize.width ? PropertiesListUtil.getPropertyDisplayString(model.pageSize.width, StringResources.get('pixelUnit'), true) : '';
			const heightValue = model.pageSize && model.pageSize.height ? PropertiesListUtil.getPropertyDisplayString(model.pageSize.height, StringResources.get('pixelUnit'), true) : '';
			return {
				width: widthValue,
				height: heightValue,
				preset: this._getPageSizePreset(widthValue, heightValue)
			};
		},

		onPageSizeChange: function() {
			const currentValues = this._getPageSizeDisplayValues();
			this.eventRouter.trigger('properties:setValue', { propertyName: ['pageSizeSplit', 'pageSizeWidth'], value: currentValues.width });
			this.eventRouter.trigger('properties:setValue', { propertyName: ['pageSizeSplit', 'pageSizeHeight'], value: currentValues.height });
			this.eventRouter.trigger('properties:setValue', { propertyName: 'pageSizePreset', value: currentValues.preset });
		},

		_getPageSizeSpec: function() {
			const defaultValues = this._getPageSizeDisplayValues();

			const properties = [{
				'type': 'SectionLabel',
				'id': 'pageSizeLabel',
				'name': 'pageSizeLabel',
				'label': StringResources.get('propPageSizeLabel'),
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas')
			}, {
				'type': 'DropDown',
				'label': StringResources.get('propPageSizePreset'),
				'ariaLabel': StringResources.get('propPageSizePresetAriaLabel'),
				'name': 'pageSizePreset',
				'id': 'pageSizePreset',
				'readOnly': false,
				'defaultValue': defaultValues.preset,
				'options': [
					{
						label: StringResources.get('propPageSizePresetCustom'),
						value: 'custom'
					}, {
						label: StringResources.get('propPageSizePreset16x9'),
						value: '16:9'
					}, {
						label: StringResources.get('propPageSizePreset4x3'),
						value: '4:3'
					}, {
						label: StringResources.get('letter'),
						value: 'letter'
					}, {
						label: StringResources.get('legal'),
						value: 'legal'
					}, {
						label: StringResources.get('a4'),
						value: 'a4'
					}, {
						label: StringResources.get('tabloid'),
						value: 'tabloid'
					}, {
						label: StringResources.get('propPageSizePresetInfographic'),
						value: 'infographic'
					}
				],
				'onChange': this._onChangePageSizePreset.bind(this),
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas'),
				sectionPosition: 1
			},{
				type: 'ToggleButton',
				label: StringResources.get('fitPageText'),
				name: 'fitPage',
				id: 'fitPage',
				checked: this.getFitPage(),
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas'),
				onChange: (propertyName, propertyValue) => {
					this.model.set({
						'fitPage': propertyValue
					});
				}
			},{
				'type': 'Split',
				'name': 'pageSizeSplit',
				'id': 'pageSizeSplit',
				'tabName': StringResources.get('tabName_general'),
				'sectionName': StringResources.get('sectionName_canvas'),
				'items': [{
					align: 'left',
					items: [{
						'type': 'InputLabel',
						'label': StringResources.get('propPageSizeWidth'),
						'ariaLabel': StringResources.get('propPageSizeWidthAriaLabel'),
						'name': 'pageSizeWidth',
						'id': 'pageSizeWidth',
						'readOnly': false,
						'value': defaultValues.width,
						'onChangeValueHold': defaultValues.width,
						'multiline': true,
						'handleReturnKey': true,
						'onChange': this._wrapPageSizeTextProperty()
					}]
				}, {
					align: 'right',
					items: [{
						'type': 'InputLabel',
						'label': StringResources.get('propPageSizeHeight'),
						'ariaLabel': StringResources.get('propPageSizeHeightAriaLabel'),
						'name': 'pageSizeHeight',
						'id': 'pageSizeHeight',
						'readOnly': false,
						'value': defaultValues.height,
						'onChangeValueHold': defaultValues.height,
						'multiline': true,
						'handleReturnKey': true,
						'onChange': this._wrapPageSizeTextProperty()
					}]
				}]
			}];
			return properties;
		},

		_onChangePageSizePreset: function(id, value) {
			const presetValues = PAGESIZES[value];
			if (presetValues) {
				const payloadData = {
					undoRedoTransactionId: _.uniqueId('layout_layoutPageSizeChange_')
				};
				const model = this.model;
				if (model.get('layoutPositioning') !== 'absolute') {
					const fitPageValue = (value === '16:9' || value === '4:3');
					model.set({
						'fitPage': fitPageValue
					}, {
						payloadData: payloadData
					});
					this.eventRouter.trigger('properties:setValue', { propertyName: 'fitPage', value: fitPageValue });
				}
				if (!model.pageSize || model.pageSize.width !== presetValues.width || model.pageSize.height !== presetValues.height) {
					this._setPageSize({
						width: presetValues.width,
						height: presetValues.height
					},
					payloadData.undoRedoTransactionId
					);
				}
			}
		},

		getFitPage: function() {
			let fitPage = false;
			if (this.model.getValueFromSelfOrParent('layoutPositioning') !== 'absolute') {
				fitPage = this.model.getValueFromSelfOrParent('fitPage') || false;
			}
			return fitPage;
		},

		_fetchThemes: function() {
			if (!this._themeListing) {
				this._themeListing = [];
				return ClassFactory.loadModule('text!dashboard-core/js/lib/@waca/dashboard-common/dist/themes/themeListing.json')
					.then((themeListing) => {
						const themesInfo = JSON.parse(themeListing);

						_.each(themesInfo.themes, (theme) => {
							this._themeListing.push({
								label: StringResources.get(theme.name + 'Label'),
								value: theme.name
							});
						});

						return this._themeListing;
					}, () => {
						this._themeListing = [];
						return this._themeListing;
					});
			} else {
				return Promise.resolve(this._themeListing);
			}
		},

		/**
		 * Reduces this view and all its descendants to a single value.
		 *
		 * @param {function} callback function(view, accumulator)
		 * @param {object} acc accumulator.
		 *
		 * returns the accumulated value returned by the last invocation of callback.
		 */
		reduce: function(callback, acc){
			acc = callback(this, acc);
			if( this.model.items ) {
				this.model.items.forEach(function(model){
					const view = this.layoutController.getLayoutView(model.id);
					if (view && view.reduce) {
						acc = view.reduce(callback, acc);
					}
				}, this);
			}
			return acc;
		},

		/**
		 * @param {Boolean} loading indicating if the loading animation should be displayed.
		 */
		setLoading: function(loading) {
			if (!this._$loadingIndicatorBlocker && !loading) {
				// if we are called for the first time with false, don't bother loading the animation at all.
				return Promise.resolve(null);
			}

			if(!this._$loadingIndicatorBlocker) {
				this._$loadingIndicatorBlocker = $('<div></div>');
			}
			if(loading){
				this.$el.append(this._$loadingIndicatorBlocker);
				ReactDOM.render(CenterLoadingSpinner({ size: 'normal', variant: 'circle' }), this._$loadingIndicatorBlocker[0]);
			} else {
				ReactDOM.unmountComponentAtNode(this._$loadingIndicatorBlocker[0]);
				this._$loadingIndicatorBlocker.remove();
				this._$loadingIndicatorBlocker = null;
			}
			return Promise.resolve(null);
		},

		/** If the layout has 'subviews' (ie. tabs or scenes), return the id of the current one. */
		getSelectedSubViewId: function() {
			// sub-classes are expected to implement this as appropriate.
			return this.id;
		},

		/** If the layout has 'subviews' (ie. tabs or scenes), return the title of the current one. */
		getSelectedSubViewTitle: function() {
			// sub-classes are expected to implement this as appropriate.
			return null;
		},

		/**
		 * Get layout ratio from parent layout
		 *
		 * Note: the method has a limitation.
		 * The method only supports horizontal layout ratios, so the Y axis
		 * ratio (height) will always be fixed to 1.
		 * This should be implemented when vertical ratios are needed. (TODO)
		 *
		 * @return {Object} ratio; has properties `width` and `height` with the appropriate ratios
		 */
		getSelfRatio: function() {
			const childCount = this.parentLayout.model
				.getVisualizations().length || 1;
			const width = 1 / childCount;
			const height = 1;

			return {
				width,
				height
			};
		},

		/**
		 * Get parent layout API
		 *
		 * @return {Object}
		 */
		getParentLayout: function() {
			return this.parentLayout.getAPI();
		},

		/**
		 * Get layout API
		 *
		 * @return {LayoutApi} layout API
		 */
		getAPI: function() {
			return this.api.getAPI();
		},

		/**
		 * Render layout
		 *
		 * Note:
		 * historically we were calling onShow when we needed a widget to render.
		 * renaming this across so many components is hard right now, but
		 * let's try to use `render` instead of `onShow`.
		 */
		render: function(options) {
			this.onShow(options);
		}
	});
	return BaseLayout;
});
